/**
 * Panel de Finance360 Ops: lo que consumen los clientes conectados y lo que hay que atender.
 * Los datos los lee Ops (Cost Management, Graph, Acronis, Sophos); aquí solo se muestran.
 */
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Loader2, RefreshCw, X } from 'lucide-react';
import { opsApi } from '../../../utils/api';
import useEscClose from '../../../hooks/useEscClose';

const MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'setiembre', 'octubre', 'noviembre', 'diciembre'];
const nombreMes = (ym) => (ym ? MES[Number(ym.slice(5, 7)) - 1] : '');
const usd = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (a, b) => (a > 0 && b != null ? (b - a) / a * 100 : null);
const NIVEL = { alta: ['Urgente', 'border-l-red-500 text-red-600'], media: ['Mejora', 'border-l-amber-500 text-amber-700'], baja: ['Aviso', 'border-l-gray-300 text-gray-500'] };

function Variacion({ v }) {
  if (v == null) return <span className="text-gray-400">—</span>;
  if (Math.abs(v) < 0.05) return <span className="text-gray-400">=</span>;
  return <span className={v > 0 ? 'text-amber-700' : 'text-emerald-700'}>{v > 0 ? '▲ +' : '▼ −'}{Math.abs(v).toFixed(1)} %</span>;
}

export default function PanelOps({ irARevision }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [filtro, setFiltro] = useState('todas');
  const [cliente, setCliente] = useState(null);

  const cargar = async () => {
    setCargando(true); setError(null);
    try { setD((await opsApi.panel()).data.data); } catch (e) { setError(e.response?.data?.error || 'No se pudo leer el panel de Finance360 Ops'); }
    finally { setCargando(false); }
  };
  useEffect(() => { cargar(); }, []);

  if (!d) return (
    <div className="py-24 flex flex-col items-center gap-3 text-gray-600">
      {error ? <><p className="font-semibold text-gray-900">{error}</p><button onClick={cargar} className="inline-flex items-center gap-1.5 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"><RefreshCw className="h-4 w-4" /> Reintentar</button></>
        : <span className="inline-flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Leyendo el consumo de los clientes…</span>}
    </div>
  );

  const { periodos: p, resumen: r } = d;
  const fuentes = ['Azure', 'Microsoft 365', 'Acronis', 'Sophos'];
  const alertas = d.alertas.filter(a => filtro === 'todas' || a.fuente === filtro);
  const maxServ = d.servicios[0]?.costo || 1;
  const crecen = d.azure.filter(a => a.pronostico != null && a.cerrado > 0).map(a => ({ ...a, v: pct(a.cerrado, a.pronostico) })).filter(a => a.v > 0).sort((x, y) => y.v - x.v).slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          [`Azure · ${nombreMes(p.enCurso)} a la fecha`, usd(r.enCurso), `día ${p.dia} de ${p.dias} · ${r.conAzure} cliente${r.conAzure === 1 ? '' : 's'}`],
          ['Pronóstico de cierre', usd(r.pronostico), <Variacion key="v" v={pct(r.cerrado, r.pronostico)} />, `vs ${nombreMes(p.cerrado)}`],
          [`Azure · ${nombreMes(p.cerrado)} cerrado`, usd(r.cerrado), <Variacion key="v" v={pct(r.anterior, r.cerrado)} />, `vs ${nombreMes(p.anterior)}`],
          ['Alertas', d.alertas.length, (n => `${n} urgente${n === 1 ? '' : 's'}`)(d.alertas.filter(a => a.nivel === 'alta').length)],
          ['Clientes conectados', r.clientes, `Azure ${r.conAzure} · 365 ${r.conM365} · Acronis ${r.conAcronis} · Sophos ${r.conSophos}`],
        ].map(([t, v, s, s2]) => (
          <div key={t} className="bg-white border border-gray-200 rounded-xl px-4 py-3 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 truncate">{t}</p>
            <p className="text-xl font-semibold tabular-nums mt-0.5">{v}</p>
            <p className="text-xs text-gray-500 truncate">{s}{s2 ? <span className="ml-1">{s2}</span> : null}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-4">
        <section className="bg-white border border-gray-200 rounded-xl p-4 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div><h3 className="font-semibold text-gray-900">Alertas de mejora</h3><p className="text-xs text-gray-500">Costos que se disparan, recursos que sobran y riesgos</p></div>
            <div className="flex gap-1 flex-wrap">
              {['todas', ...fuentes].map(f => (
                <button key={f} onClick={() => setFiltro(f)} className={clsx('text-xs font-semibold border rounded-full px-2.5 py-1', filtro === f ? 'border-indigo-600 text-indigo-700' : 'border-gray-200 text-gray-500')}>
                  {f === 'todas' ? 'Todas' : f} <span className="opacity-70">{f === 'todas' ? d.alertas.length : d.alertas.filter(a => a.fuente === f).length}</span>
                </button>
              ))}
              <button onClick={cargar} disabled={cargando} title="Volver a leer" className="border border-gray-200 rounded-full px-2 py-1 text-gray-500"><RefreshCw className={clsx('h-3.5 w-3.5', cargando && 'animate-spin')} /></button>
            </div>
          </div>
          {alertas.length === 0 ? <p className="text-sm text-gray-500 py-8 text-center">Sin alertas{filtro !== 'todas' ? ` de ${filtro}` : ''}.</p> : (
            <div className="space-y-2">
              {alertas.map((a, i) => (
                <button key={i} onClick={() => a.clienteId && setCliente(a.clienteId)}
                  className={clsx('w-full text-left grid grid-cols-[70px_minmax(0,1fr)_auto] md:grid-cols-[70px_minmax(0,1fr)_110px_120px] gap-3 items-center border border-gray-200 border-l-[3px] rounded-lg px-3 py-2 hover:border-indigo-400', NIVEL[a.nivel][1].split(' ')[0])}>
                  <span className={clsx('text-[11px] font-bold uppercase tracking-wide', NIVEL[a.nivel][1].split(' ').slice(1).join(' '))}>{NIVEL[a.nivel][0]}</span>
                  <span className="text-sm min-w-0"><b>{a.cliente}</b> · {a.tipo}<span className="block text-xs text-gray-500 truncate" title={a.detalle}>{a.detalle}</span></span>
                  <span className="hidden md:inline-block justify-self-start text-[11px] font-bold text-gray-500 border border-gray-200 rounded-md px-1.5 py-0.5">{a.fuente}</span>
                  <span className="text-right font-semibold tabular-nums text-sm">
                    {a.impacto != null ? <>{a.sentido === 'baja' ? '−' : '+'}{usd(a.impacto)}<span className="block text-[11px] font-normal text-gray-500">al mes</span></>
                      : a.cantidad != null ? <>{a.cantidad}<span className="block text-[11px] font-normal text-gray-500">sin asignar</span></> : <span className="text-xs font-normal text-gray-500">riesgo</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-gray-200 rounded-xl p-4 min-w-0 space-y-5">
          <div>
            <h3 className="font-semibold text-gray-900">Dónde se va el gasto de Azure</h3><p className="text-xs text-gray-500 mb-3">{nombreMes(p.cerrado)}, por servicio, todos los clientes</p>
            {d.servicios.length === 0 ? <p className="text-sm text-gray-500">Sin clientes con Azure conectado.</p> : (
              <div className="space-y-1.5">
                {d.servicios.slice(0, 8).map(s => (
                  <div key={s.servicio} className="grid grid-cols-[140px_minmax(0,1fr)_100px] gap-3 items-center text-sm">
                    <span className="truncate" title={s.servicio}>{s.servicio}</span>
                    <span className="h-2.5 border border-gray-200 rounded overflow-hidden"><i className="block h-full border-r-2 border-sky-500 bg-sky-500/10" style={{ width: `${s.costo / maxServ * 100}%` }} /></span>
                    <b className="text-right tabular-nums">{usd(s.costo)}</b>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Los que más crecen</h3><p className="text-xs text-gray-500 mb-2">Pronóstico de {nombreMes(p.enCurso)} contra {nombreMes(p.cerrado)}</p>
            {crecen.length === 0 ? <p className="text-sm text-gray-500">Ningún cliente va camino a subir.</p> : crecen.map(a => (
              <button key={a.clienteId} onClick={() => setCliente(a.clienteId)} className="w-full grid grid-cols-[minmax(0,1fr)_90px_100px] gap-3 items-center text-sm py-1 text-left hover:text-indigo-700">
                <span className="truncate">{a.cliente}</span><span className="text-amber-700 tabular-nums">▲ +{a.v.toFixed(1)} %</span><b className="text-right tabular-nums text-amber-700">+{usd(a.pronostico - a.cerrado)}</b>
              </button>
            ))}
          </div>
          {d.acronis.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900">Acronis · almacenamiento</h3><p className="text-xs text-gray-500 mb-2">Usado contra el límite asignado</p>
              {d.acronis.filter(x => x.pct != null).sort((x, y) => y.pct - x.pct).slice(0, 5).map(x => (
                <div key={x.clienteId} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_60px] gap-3 items-center text-sm py-1">
                  <span className="truncate">{x.cliente}</span>
                  <span className="h-2.5 border border-gray-200 rounded overflow-hidden"><i className={clsx('block h-full border-r-2', x.pct >= 90 ? 'border-red-500 bg-red-500/10' : x.pct >= 80 ? 'border-amber-500 bg-amber-500/10' : 'border-emerald-500 bg-emerald-500/10')} style={{ width: `${Math.min(x.pct, 100)}%` }} /></span>
                  <b className="text-right tabular-nums">{x.pct.toFixed(0)} %</b>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900">Azure por cliente</h3>
        <p className="text-xs text-gray-500 mb-3">{nombreMes(p.anterior)} · {nombreMes(p.cerrado)} · {nombreMes(p.enCurso)} a la fecha y pronóstico de cierre. Clic en la fila para ver el detalle.</p>
        {d.azure.length === 0 ? <p className="text-sm text-gray-500 py-6 text-center">Ningún cliente tiene Azure conectado todavía. Se conecta desde la revisión de borradores.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <th className="px-3 py-2 font-semibold">Cliente</th><th className="px-3 py-2 font-semibold text-right capitalize">{nombreMes(p.anterior)}</th><th className="px-3 py-2 font-semibold text-right capitalize">{nombreMes(p.cerrado)}</th>
              <th className="px-3 py-2 font-semibold text-right">Variación</th><th className="px-3 py-2 font-semibold text-right capitalize">{nombreMes(p.enCurso)} a la fecha</th><th className="px-3 py-2 font-semibold text-right">Pronóstico</th><th className="px-3 py-2 font-semibold text-right">Alertas</th>
            </tr></thead>
            <tbody>{d.azure.map(a => { const n = d.alertas.filter(x => x.clienteId === a.clienteId).length; return (
              <tr key={a.clienteId} onClick={() => setCliente(a.clienteId)} className="border-b border-gray-100 cursor-pointer hover:bg-gray-50">
                <td className="px-3 py-2 font-semibold">{a.cliente}{a.error && <span className="block text-xs font-normal text-red-600 truncate max-w-[340px]" title={a.error}>{a.error}</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{usd(a.anterior)}</td><td className="px-3 py-2 text-right tabular-nums">{usd(a.cerrado)}</td>
                <td className="px-3 py-2 text-right tabular-nums"><Variacion v={pct(a.anterior, a.cerrado)} /></td>
                <td className="px-3 py-2 text-right tabular-nums">{usd(a.enCurso)}</td>
                <td className={clsx('px-3 py-2 text-right tabular-nums', a.pronostico > (a.cerrado || 0) * 1.1 && 'text-amber-700')}>{usd(a.pronostico)}</td>
                <td className="px-3 py-2 text-right">{n ? <span className="text-xs font-semibold border border-amber-400 text-amber-700 rounded-full px-2">{n}</span> : <span className="text-gray-300">—</span>}</td>
              </tr>); })}</tbody>
          </table></div>
        )}
      </section>

      {cliente && <DetalleCliente d={d} clienteId={cliente} onClose={() => setCliente(null)} irARevision={irARevision} />}
    </div>
  );
}

function DetalleCliente({ d, clienteId, onClose, irARevision }) {
  useEscClose(onClose);
  const az = d.azure.find(a => a.clienteId === clienteId);
  const lic = d.licencias.find(a => a.clienteId === clienteId);
  const acr = d.acronis.find(a => a.clienteId === clienteId);
  const sop = d.sophos.find(a => a.clienteId === clienteId);
  const al = d.alertas.filter(a => a.clienteId === clienteId);
  const nombre = az?.cliente || lic?.cliente || acr?.cliente || sop?.cliente || al[0]?.cliente;
  const p = d.periodos;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-5" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-5xl max-h-[calc(100vh-40px)] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200">
          <div><h2 className="text-lg font-bold text-gray-900">{nombre}</h2>
            {az && <p className="text-xs text-gray-500">Azure: {nombreMes(p.anterior)} {usd(az.anterior)} · {nombreMes(p.cerrado)} {usd(az.cerrado)} · {nombreMes(p.enCurso)} a la fecha {usd(az.enCurso)} · pronóstico {usd(az.pronostico)}</p>}</div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center border border-gray-200 rounded-lg text-gray-500" aria-label="Cerrar"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5 overflow-auto">
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <p className="px-4 py-2.5 text-sm font-semibold border-b border-gray-100">Azure · {nombreMes(p.cerrado)} por servicio</p>
            {az?.servicios?.length ? <table className="w-full text-sm"><tbody>{az.servicios.map(s => <tr key={s.servicio} className="border-b border-gray-50"><td className="px-4 py-1.5">{s.servicio}</td><td className="px-4 py-1.5 text-right tabular-nums">{usd(s.costo)}</td></tr>)}</tbody></table>
              : <p className="px-4 py-4 text-sm text-gray-500">{az ? 'Sin consumo leído.' : 'Azure no está conectado.'}</p>}
          </div>
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <p className="px-4 py-2.5 text-sm font-semibold border-b border-gray-100">Alertas</p>
              {al.length ? al.map((a, i) => <div key={i} className="px-4 py-2 border-b border-gray-50 text-sm"><b>{a.tipo}</b> <span className="text-xs text-gray-500">· {a.fuente}</span><p className="text-xs text-gray-600">{a.detalle}</p></div>)
                : <p className="px-4 py-4 text-sm text-gray-500">Sin alertas.</p>}
            </div>
            {lic && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <p className="px-4 py-2.5 text-sm font-semibold border-b border-gray-100">Microsoft 365 · {lic.compradas} compradas, {lic.asignadas} asignadas</p>
                {lic.sobran.length ? <table className="w-full text-sm"><tbody>{lic.sobran.map(s => <tr key={s.producto} className="border-b border-gray-50"><td className="px-4 py-1.5">{s.producto}</td><td className="px-4 py-1.5 text-right tabular-nums text-amber-700">{s.sinAsignar} sin asignar de {s.compradas}</td></tr>)}</tbody></table>
                  : <p className="px-4 py-3 text-sm text-gray-500">Todas las licencias están asignadas.</p>}
              </div>
            )}
            {(acr || sop) && (
              <div className="border border-gray-200 rounded-xl px-4 py-3 text-sm space-y-1">
                {acr && <p><b>Acronis:</b> {acr.usado?.toLocaleString('es-CR')} GB usados{acr.limite ? ` de ${acr.limite.toLocaleString('es-CR')} (${acr.pct?.toFixed(0)} %)` : ''}</p>}
                {sop && <p><b>Sophos:</b> {sop.computadoras ?? '—'} computadoras · {sop.servidores ?? '—'} servidores</p>}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end px-5 py-3 border-t border-gray-200">
          <button onClick={() => { onClose(); irARevision(); }} className="px-3.5 py-2 text-sm font-semibold border-[1.5px] border-indigo-600 text-indigo-700 rounded-lg">Ir a la revisión de borradores</button>
        </div>
      </div>
    </div>
  );
}
